import React, { useEffect, useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';

import DialogDelete from '../../controls/dialogs/DialogDelete';
import DialogUpdateUserRole from '../../controls/dialogs/DialogUpdateUserRole';
import { userManagementService } from '../../../services/user-management.service';
import { UserListItem } from '../../../models/user/userListItem';
import { authService } from '../../../services/auth.service';
import { getDefaultRoleFromLegacyFlag, OrganisatieUserRole, updateRoleOptions } from '../../../models/user/userRole';


const OrganisatieUsersActionMenu = ({ 
  params,
  organisatieId,
  onUserUpdate
}: { 
  params: GridRenderCellParams<UserListItem>;
  organisatieId: string;
  onUserUpdate: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isAdmin, setAdmin] = useState<boolean>(false);
  const [canCrud, setCanCrud] = useState<boolean>(false);

  const [open, setOpenDialogs] = useState({
    delete: false,
    updateRole: false,
  });

  const currentRole = (params.row.userType ?? getDefaultRoleFromLegacyFlag(params.row.keyUser)) as OrganisatieUserRole;
  const canManageTargetUser = canCrud && (isAdmin || (currentRole !== 'keyUser' && currentRole !== 'admin'));
  const roleOptions = updateRoleOptions.filter((role) => isAdmin || role !== 'keyUser');

  useEffect(() => {
    setIsAdmin();
  }, []);

  const setIsAdmin = async () => {
    const isAdmin = await authService.isAdmin();
    setAdmin(isAdmin);
    setCanCrud(authService.canCrud());
  }

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
    handleCloseMenu();
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Verwijderen user

  const handleConfirmDelete = () => {
    handleDelete();
    closeDialog('delete');
  };

  // event
  const handleDelete = async () => {
    if (!organisatieId) {
      console.error('Organisatie id is null');
      return;
    }

    if (!params.row.email) {
      console.error('Email is null');
      return;
    }

    try {
      const response = await userManagementService.deleteUserFromOrganisatie(organisatieId, params.row.email, currentRole);

      if (response.ok) {
        setTimeout(() => onUserUpdate(), 2000);
      }
    }
    catch (error) {
      // Handle error (e.g., show an error message)
      console.error('Error deleting user:', error);
    }
  }

  const handleConfirmUpdateRole = async (nextRole: OrganisatieUserRole) => {
    if (!organisatieId || !params.row.email) {
      return;
    }

    try {
      const response = await userManagementService.updateUserRoleForOrganisatie(
        organisatieId,
        params.row.email,
        currentRole,
        nextRole,
      );

      if (response?.ok) {
        closeDialog('updateRole');
        setTimeout(() => onUserUpdate(), 2000);
      }
    }
    catch (error) {
      console.error('Error updating role:', error);
    }
  }

  return (
    <>
      {canManageTargetUser && (
        <>
        <IconButton onClick={(e) => handleOpenMenu(e)}>
          <Tooltip title="Acties" placement="right" arrow>
            <Icon>more_vert</Icon>
          </Tooltip>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => openDialog('updateRole')}>
            <ListItemIcon>
              <Icon>person</Icon>
            </ListItemIcon>
            Rol aanpassen
          </MenuItem>
          <MenuItem onClick={() => openDialog('delete')}>
            <ListItemIcon>
              <Icon>delete</Icon>
            </ListItemIcon>
            Verwijderen
          </MenuItem>
        </Menu>
        </>
      )}

      <DialogDelete
        open={open.delete}
        name={params.row.email || undefined}
        onClose={() => closeDialog('delete')}
        onDelete={handleConfirmDelete}
      />

      <DialogUpdateUserRole
        open={open.updateRole}
        email={params.row.email}
        currentRole={currentRole}
        allowedRoles={roleOptions}
        onClose={() => closeDialog('updateRole')}
        onSubmit={handleConfirmUpdateRole}
      />
    </>
  );
};

export default OrganisatieUsersActionMenu;
